import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { useStoreActions } from 'easy-peasy';
import { useAuthContext } from './AuthContext';
import websocketService from 'services/websocket';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

function isRepairerHiddenPumpStatus(status) {
  const n = Number(status);
  // Matches backend Pump.REPAIRER_HIDDEN_PUMP_STATUSES — only permanently removed
  return n === 7;
}

/** Matches backend Pump.MAINTENANCE_HIDDEN_PUMP_STATUSES — repair pipeline hidden from maintenance users. */
function isMaintenanceHiddenPumpStatus(status) {
  const n = Number(status);
  return n === 3 || n === 4 || n === 5 || n === 6;
}

function shouldHidePumpForCurrentRole(status, role) {
  if (role === 3) return isRepairerHiddenPumpStatus(status);
  if (role === 5) return isMaintenanceHiddenPumpStatus(status);
  return false;
}

export const WebSocketProvider = ({ children }) => {
  const { token, isAuthenticated, userRole } = useAuthContext();
  const { updatePumpInState, addPump, removePump, getPump } = useStoreActions((actions) => actions.pumps);
  const { updatePoolInState, addPool, removePool } = useStoreActions((actions) => actions.pools);
  
  const [pumpsConnected, setPumpsConnected] = useState(false);
  const [poolsConnected, setPoolsConnected] = useState(false);

  /**
   * Handle pump updates from WebSocket
   */
  const handlePumpMessage = useCallback((data) => {
    if (data.type === 'connection_established') {
      console.log('[WebSocket] Pump connection established');
      return;
    }

    if (data.type === 'pump_update') {
      const { action, pump } = data;

      console.log(`[WebSocket] Pump ${action}:`, pump);

      const applyPumpUpdateOrDropForRole = (payload) => {
        if (payload && shouldHidePumpForCurrentRole(payload.status, userRole)) {
          removePump(payload.id);
        } else if (payload) {
          updatePumpInState(payload);
        }
      };
      
      switch (action) {
        case 'created':
          if (!pump) break;
          if (!shouldHidePumpForCurrentRole(pump.status, userRole)) {
            addPump(pump);
          }
          break;
        case 'updated':
          applyPumpUpdateOrDropForRole(pump);
          break;
        case 'deleted':
          removePump(pump.id);
          break;
        case 'new_comment_requires_acknowledgement':
          // New comment that requires acknowledgement - refresh pump data
          console.log('[WebSocket] New comment requires acknowledgement for pump:', pump.id);
          if (pump.id) {
            // Fetch updated pump data to get the new unacknowledged_comments_count
            getPump(pump.id).then((updatedPump) => {
              if (!updatedPump) {
                if (userRole === 3 || userRole === 5) {
                  removePump(pump.id);
                }
                return;
              }
              if (shouldHidePumpForCurrentRole(updatedPump.status, userRole)) {
                removePump(pump.id);
                return;
              }
              updatePumpInState(updatedPump);
            });
          }
          break;
        case 'comment_acknowledged':
          // Comment was acknowledged - refresh pump data
          console.log('[WebSocket] Comment acknowledged for pump:', pump.id);
          if (pump.id) {
            // Fetch updated pump data to reflect the acknowledgement
            getPump(pump.id).then((updatedPump) => {
              if (!updatedPump) {
                if (userRole === 3 || userRole === 5) {
                  removePump(pump.id);
                }
                return;
              }
              if (shouldHidePumpForCurrentRole(updatedPump.status, userRole)) {
                removePump(pump.id);
                return;
              }
              updatePumpInState(updatedPump);
            });
          }
          break;
        default:
          console.warn('[WebSocket] Unknown pump action:', action);
      }
    }
  }, [addPump, updatePumpInState, removePump, getPump, userRole]);

  /**
   * Handle pool updates from WebSocket
   */
  const handlePoolMessage = useCallback((data) => {
    if (data.type === 'connection_established') {
      console.log('[WebSocket] Pool connection established');
      return;
    }

    if (data.type === 'pool_update') {
      const { action, pool } = data;
      
      console.log(`[WebSocket] Pool ${action}:`, pool);
      
      switch (action) {
        case 'created':
          addPool(pool);
          break;
        case 'updated':
          updatePoolInState(pool);
          break;
        case 'deleted':
          removePool(pool.id);
          break;
        default:
          console.warn('[WebSocket] Unknown pool action:', action);
      }
    }
  }, [addPool, updatePoolInState, removePool]);

  /**
   * Connect to WebSockets when authenticated
   */
  useEffect(() => {
    console.log('[WebSocket] useEffect triggered - isAuthenticated:', isAuthenticated, 'token:', token ? 'exists' : 'null');
    
    if (!isAuthenticated || !token) {
      // Disconnect if not authenticated
      console.log('[WebSocket] Not authenticated or no token, disconnecting');
      websocketService.disconnectAll();
      setPumpsConnected(false);
      setPoolsConnected(false);
      return;
    }

    console.log('[WebSocket] Setting up connections');

    // Connect to pumps WebSocket
    websocketService.connect(
      'pumps',
      token,
      handlePumpMessage,
      () => setPumpsConnected(true),
      () => setPumpsConnected(false)
    );

    // Connect to pools WebSocket
    websocketService.connect(
      'pools',
      token,
      handlePoolMessage,
      () => setPoolsConnected(true),
      () => setPoolsConnected(false)
    );

    // Cleanup on unmount or when auth changes
    return () => {
      console.log('[WebSocket] Cleaning up connections');
      websocketService.disconnectAll();
    };
  }, [isAuthenticated, token, handlePumpMessage, handlePoolMessage]);

  const value = {
    pumpsConnected,
    poolsConnected,
    isConnected: pumpsConnected && poolsConnected,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

